import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useBrand } from '../context/BrandContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  FileText, 
  Image, 
  Volume2, 
  Loader2,
  Clock,
  Filter,
  Search,
  Eye,
  Download,
  Copy,
  Check
} from 'lucide-react';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContentLibrary() {
  const { currentBrand, brands } = useBrand();
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [websiteFilter, setWebsiteFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchContent();
  }, [typeFilter, websiteFilter]);

  const fetchContent = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (typeFilter !== 'all') params.append('content_type', typeFilter);
      if (websiteFilter !== 'all') params.append('website', websiteFilter);
      params.append('limit', '100');

      const response = await axios.get(`${API}/content?${params.toString()}`, { withCredentials: true });
      // Ensure content is always an array
      setContent(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch content:', error);
      toast.error('Failed to load content');
      setContent([]);
    } finally {
      setLoading(false);
    }
  };

  const openDetail = async (item) => {
    setSelectedItem(item);
    setDetailLoading(true);
    setDetailData(null);

    try {
      const endpoint = `${API}/content/${item.type}/${item.id}`;
      const response = await axios.get(endpoint, { withCredentials: true });
      setDetailData(response.data);
    } catch (error) {
      console.error('Failed to fetch detail:', error);
      toast.error('Failed to load content details');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCopy = async (text) => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = (base64, type, filename) => {
    const mimeTypes = {
      image: 'image/png',
      audio: 'audio/mpeg'
    };
    const link = document.createElement('a');
    link.href = `data:${mimeTypes[type]};base64,${base64}`;
    link.download = filename;
    link.click();
    toast.success('Downloaded!');
  };

  const getContentIcon = (type) => {
    switch (type) {
      case 'script': return FileText;
      case 'image': return Image;
      case 'audio': return Volume2;
      default: return FileText;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'script': return '#8B5CF6';
      case 'image': return '#10B981';
      case 'audio': return '#3B82F6';
      default: return currentBrand.primary;
    }
  };

  // Ensure content is always an array before filtering
  const safeContent = Array.isArray(content) ? content : [];
  const filteredContent = safeContent.filter(item => {
    if (searchQuery) {
      return item.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
             item.preview?.toLowerCase().includes(searchQuery.toLowerCase());
    }
    return true;
  });

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col lg:flex-row lg:items-center justify-between gap-4"
      >
        <div>
          <h1 className="text-3xl font-bold text-zinc-100 font-['Manrope']">Content Library</h1>
          <p className="text-zinc-400 mt-1">Browse and manage all your generated content</p>
        </div>
        <div className="flex items-center gap-2 text-sm text-zinc-500">
          <span>{filteredContent.length} items</span>
        </div>
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="flex flex-col sm:flex-row gap-4"
      >
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            data-testid="library-search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search content..."
            className="pl-10 bg-zinc-900/50 border-zinc-800 h-11"
          />
        </div>
        
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger 
            data-testid="type-filter"
            className="w-full sm:w-[180px] bg-zinc-900/50 border-zinc-800 h-11"
          >
            <Filter className="w-4 h-4 mr-2 text-zinc-500" />
            <SelectValue placeholder="Content Type" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="script">Scripts</SelectItem>
            <SelectItem value="image">Images</SelectItem>
            <SelectItem value="audio">Audio</SelectItem>
          </SelectContent>
        </Select>

        <Select value={websiteFilter} onValueChange={setWebsiteFilter}>
          <SelectTrigger 
            data-testid="website-filter"
            className="w-full sm:w-[200px] bg-zinc-900/50 border-zinc-800 h-11"
          >
            <SelectValue placeholder="Website" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all">All Websites</SelectItem>
            {Object.values(brands).map((brand) => (
              <SelectItem key={brand.id} value={brand.id}>
                <div className="flex items-center gap-2">
                  <div 
                    className="w-2 h-2 rounded-full"
                    style={{ background: brand.primary }}
                  />
                  {brand.name}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </motion.div>

      {/* Content Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
        </div>
      ) : filteredContent.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-16 h-16 rounded-2xl bg-zinc-900 flex items-center justify-center mb-4">
            <FileText className="w-8 h-8 text-zinc-700" />
          </div>
          <p className="text-zinc-400 text-lg mb-2">No content found</p>
          <p className="text-zinc-600 text-sm">
            {searchQuery ? 'Try a different search term' : 'Start creating content to see it here'}
          </p>
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4"
        >
          {filteredContent.map((item, i) => {
            const ContentIcon = getContentIcon(item.type);
            const typeColor = getTypeColor(item.type);
            
            return (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                onClick={() => openDetail(item)}
                className="group p-5 rounded-xl bg-zinc-900/50 backdrop-blur-xl border border-zinc-800 hover:border-zinc-700 cursor-pointer transition-all duration-300 hover:-translate-y-1"
              >
                <div className="flex items-start gap-3 mb-3">
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: `${typeColor}20` }}
                  >
                    <ContentIcon className="w-5 h-5" style={{ color: typeColor }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-zinc-200 truncate">{item.title}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span 
                        className="text-xs px-2 py-0.5 rounded-full capitalize"
                        style={{ background: `${typeColor}20`, color: typeColor }}
                      >
                        {item.type}
                      </span>
                      {item.website !== 'N/A' && (
                        <span className="text-xs text-zinc-500">{item.website}</span>
                      )}
                    </div>
                  </div>
                </div>
                
                <p className="text-sm text-zinc-500 line-clamp-2 mb-3">{item.preview}</p>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1 text-xs text-zinc-600">
                    <Clock className="w-3 h-3" />
                    {new Date(item.created_at).toLocaleDateString()}
                  </div>
                  <Eye className="w-4 h-4 text-zinc-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      )}

      {/* Detail Modal */}
      <Dialog open={!!selectedItem} onOpenChange={() => setSelectedItem(null)}>
        <DialogContent className="max-w-3xl bg-zinc-950 border-zinc-800 max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-zinc-100 font-['Manrope']">
              {selectedItem?.title}
            </DialogTitle>
          </DialogHeader>

          {detailLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
            </div>
          ) : detailData && (
            <div className="mt-4 space-y-4">
              {selectedItem?.type === 'script' && (
                <>
                  <div className="flex items-center gap-4 text-sm text-zinc-400">
                    <span>Website: <strong className="text-zinc-200">{detailData.website}</strong></span>
                    <span>Type: <strong className="text-zinc-200">{detailData.content_type?.replace('_', ' ')}</strong></span>
                  </div>
                  <div className="relative">
                    <pre className="p-4 rounded-lg bg-zinc-900 border border-zinc-800 text-zinc-300 text-sm whitespace-pre-wrap font-['JetBrains_Mono'] max-h-[400px] overflow-y-auto">
                      {detailData.script}
                    </pre>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCopy(detailData.script)}
                      className="absolute top-2 right-2 text-zinc-400 hover:text-zinc-200"
                    >
                      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                </>
              )}

              {selectedItem?.type === 'image' && (
                <>
                  <p className="text-sm text-zinc-400">
                    Prompt: <span className="text-zinc-300">{detailData.prompt}</span>
                  </p>
                  <div className="flex justify-center">
                    <img
                      src={`data:image/png;base64,${detailData.image_base64}`}
                      alt="Generated"
                      className="max-w-full max-h-[400px] rounded-lg border border-zinc-800"
                    />
                  </div>
                  <Button
                    onClick={() => handleDownload(detailData.image_base64, 'image', `image-${detailData.id}.png`)}
                    className="w-full"
                    style={{ background: currentBrand.primary }}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Image
                  </Button>
                </>
              )}

              {selectedItem?.type === 'audio' && (
                <>
                  <p className="text-sm text-zinc-400">
                    Text: <span className="text-zinc-300">{detailData.text}</span>
                  </p>
                  <audio
                    controls
                    src={`data:audio/mpeg;base64,${detailData.audio_base64}`}
                    className="w-full"
                  />
                  <Button
                    onClick={() => handleDownload(detailData.audio_base64, 'audio', `audio-${detailData.id}.mp3`)}
                    className="w-full"
                    style={{ background: currentBrand.primary }}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Audio
                  </Button>
                </>
              )}

              <p className="text-xs text-zinc-600 text-right">
                Created: {new Date(detailData.created_at).toLocaleString()}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
